import PropTypes from 'prop-types'
import classNames from 'classnames'
import React, { Component, Fragment } from 'react'

import { connect } from 'react-redux'

import { getLineItemCount } from 'base/reducers/cart'
import { getIsAdminUser } from 'base/reducers/session/user'
import { getCurrentPath, getLastPath } from 'base/reducers/pathHistory'
import { openCart, closeCart } from 'base/actions/synchronous'
import { logOut } from 'base/actions/asynchronous'
import { currencyAware } from 'base/components/currency_aware'
import routeTo from 'base/lib/route_to'

import Event from 'base/event'
import Hub from 'base/hub'
import SiteLocation from 'base/constants/site_location'
import Logo from 'base/react/components/logo'

import LoadingIndicator from 'desktop/components/layout/loading_indicator'
import AccountDropdownV2 from 'desktop/components/layout/account_dropdown_v2'
import Subheader from 'desktop/components/layout/subheader'
import TopLevelMenu from 'desktop/components/layout/top_level_menu'
import { Search, User } from '@everlane/icon'
import FlyoutCart from 'desktop/containers/layout/flyout_cart'
import SneakPeekLoginModal from 'desktop/components/modals/sneak_peek_login_modal'
import FollowUpModal from 'desktop/containers/modals/follow_up_modal'

import { setUserActivationStatus } from 'base/lib/trackers/google_tag_manager'
import RouterLink from 'base/components/router_link'
import Scroller from 'base/lib/scroller'
import NotificationProvider from 'base/containers/notifications/provider'
import NotificationIndicator from 'base/components/notifications/indicator'
import CartBadge from 'desktop/components/layout/cart_badge'

const HEADER_HEIGHT = 56
const SUBHEADER_HEIGHT = 56

export class HeaderV2 extends Component {
  static propTypes = {
    isLoading: PropTypes.bool,
    isSignedIn: PropTypes.bool.isRequired,
    isAdminUser: PropTypes.bool.isRequired,
    isOnSearchPage: PropTypes.bool.isRequired,
    isOnAccountPage: PropTypes.bool.isRequired,
    userName: PropTypes.string,
    menus: PropTypes.arrayOf(PropTypes.object).isRequired,
    currentPath: PropTypes.string.isRequired,
    numberOfLineItems: PropTypes.number.isRequired,
    logOut: PropTypes.func.isRequired,
    isIpad: PropTypes.bool.isRequired,
    bannerHeightOffset: PropTypes.number,
    gender: PropTypes.string,
    showHoliday: PropTypes.bool,
    lastPath: PropTypes.string,
  }

  constructor(props) {
    super(props)

    this.state = {
      isFloating: false,
      showAccountDropdown: false,
      isShowingLoginModal: false,
      isShowingSignupModal: false,
      selectedHeaderMenuItem: '',
    }

    this.renderSubheader = this.renderSubheader.bind(this)
    this.handleToggleAccountDropdown = this.handleToggleAccountDropdown.bind(this)
  }

  componentDidMount() {
    document.querySelector('.content-wrap').style.marginTop = 0
  }

  componentWillUnmount() {
    document.querySelector('.content-wrap').style.marginTop = -HEADER_HEIGHT
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isCartOpen && this.props.isCartOpen) {
      Hub.pub(Event.Navigation.CART_DROP_DOWN_OPENED)
    } else if (prevProps.isCartOpen && !this.props.isCartOpen) {
      Hub.pub(Event.Navigation.CART_DROP_DOWN_CLOSED)
    }
  }

  handleScroll(scrollOffset) {
    const floatAt = Math.max(10, this.props.bannerHeightOffset)
    const isFloating = scrollOffset > floatAt

    if (isFloating !== this.state.isFloating) {
      this.setState({ isFloating })
      this.handleDismissAccountDropdown()
    }
  }

  handleToggleAccountDropdown() {
    this.setState({
      showAccountDropdown: !this.state.showAccountDropdown,
    })

    if (this.state.isShowAccountDropdown) {
      Hub.pub(Event.Navigation.ACCOUNT_DROP_DOWN_OPENED)
    }
  }

  setSubMenu = menu => {
    this.setState({ selectedHeaderMenuItem: menu })
  }

  handleDismissAccountDropdown = () => {
    if (this.state.showAccountDropdown) {
      this.setState({ showAccountDropdown: false })
    }
  }

  handleToggleLogInModal = ({ isVisible }) => {
    this.setState({ isShowingLoginModal: isVisible })
  }

  handleToggleSignUpModal = ({ isVisible }) => {
    this.setState({ isShowingSignupModal: isVisible })
  }

  renderMenus = () => {
    const menus = this.props.menus?.filter(menu => menu?.platform !== 'mobile')

    return (
      <TopLevelMenu
        menus={menus}
        currentPath={this.props.currentPath}
        gender={this.props.gender}
        setMenu={this.setSubMenu}
        selectedMenu={this.state.selectedHeaderMenuItem}
      />
    )
  }

  renderSubheader(menu) {
    return (
      <div key={menu.name}>
        <Subheader
          categories={menu.categories}
          subheaderHeight={SUBHEADER_HEIGHT}
          hidden={menu.name !== this.state.selectedHeaderMenuItem?.name}
          selectedHeaderMenuItem={this.state.selectedHeaderMenuItem?.name}
        />
      </div>
    )
  }

  renderAccountMenu() {
    return (
      <div
        className={classNames('app-header__right-menu-container', {
          'app-header__right-menu-container--active': this.props.isOnAccountPage,
        })}
      >
        <div className="app-header__account" onClick={this.handleToggleAccountDropdown}>
          {this.props.isSignedIn && (
            <NotificationProvider>
              <NotificationIndicator />
            </NotificationProvider>
          )}

          <User width="16px" height="16px" strokeWidth={2.5} />

          <AccountDropdownV2
            onRequestLogOut={this.props.logOut}
            showAdminLink={this.props.isAdminUser}
            isSignedIn={this.props.isSignedIn}
            handleToggleAccountDropdown={this.handleToggleAccountDropdown}
            showAccountDropdown={this.state.showAccountDropdown}
            onToggleLogInModal={this.handleToggleLogInModal}
            onToggleSignUpModal={this.handleToggleSignUpModal}
          />
        </div>
      </div>
    )
  }

  renderCartMenu() {
    const { openCart, isCartOpen, numberOfLineItems } = this.props

    return (
      <CartBadge
        isBeingHovered={isCartOpen}
        openCart={openCart}
        numberOfLineItems={numberOfLineItems}
        topLevelClass="app-header__cart-badge--v2"
      />
    )
  }

  renderFlyoutCart() {
    const { isCartOpen } = this.props

    if (isCartOpen) {
      return <FlyoutCart closeCart={() => this.props.closeCart()} />
    }

    return null
  }

  renderLoginModal() {
    return (
      <SneakPeekLoginModal
        isSigningIn={this.state.isShowingLoginModal}
        isNewUser={this.state.isShowingSignupModal ? undefined : false}
        skipEmailEntry={this.state.isShowingSignupModal}
        siteLocation={SiteLocation.LEFT_HEADER}
        onRequestClose={() => {
          if (this.state.isShowingLoginModal) {
            this.handleToggleLogInModal({ isVisible: false })
          } else {
            this.handleToggleSignUpModal({ isVisible: false })
          }
        }}
      />
    )
  }

  toggleSearchWithIcon = () => {
    Hub.pub(Event.AlgoliaSearch.NAV_CLICK, {
      from: window.location.pathname,
    })

    this.props.isOnSearchPage && setTimeout(() => routeTo(this.props.lastPath || '/'), 250)
  }

  renderSearch() {
    return (
      <div
        className={classNames('app-header__right-menu-container', {
          'app-header__right-menu-container--active': this.props.isOnSearchPage,
        })}
      >
        <RouterLink
          className="app-header__search"
          href="/search"
          onClick={this.toggleSearchWithIcon}
        >
          <Search width="16px" height="16px" color="hsl(0, 0%, 15%)" strokeWidth={2.5} />
        </RouterLink>
      </div>
    )
  }

  render() {
    const className = classNames('app-header', {
      'app-header--with-shadow': this.state.isFloating,
      'app-header--no-shadow': this.props.isOnSearchPage,
      'app-header--active-right-menu': this.props.isOnSearchPage || this.props.isOnAccountPage,
    })

    const menus = this.props.menus?.filter(
      menu =>
        menu.name !== 'Visit Us' &&
        menu.name !== 'Stores' &&
        menu.name !== 'Factories' &&
        menu?.platform !== 'mobile',
    )

    const headerStyles = {
      height: HEADER_HEIGHT,
      transition: this.props.isOnSearchPage
        ? 'top 100ms cubic-bezier(0.45, 0.05, 0.55, 0.95)'
        : null,
    }

    return (
      <Fragment>
        {/* Setting throttle to 0 on Search to update nav offset as soon as scroll event fires */}
        <Scroller
          throttleDelay={this.props.isOnSearchPage ? 0 : 420}
          onChange={this.handleScroll.bind(this)}
          cancelsScroll={false}
        />
        <header
          className={className}
          style={headerStyles}
          onMouseLeave={this.handleDismissAccountDropdown}
        >
          <div className="app-header__wrapper">
            <ul className="app-header__far-left">{this.renderMenus()}</ul>
            <div className="app-header__middle">
              <div className="app-header__logo-container">
                <RouterLink href="/" style={{ fill: 'inherit' }} aria-label="Everlane">
                  <Logo />
                </RouterLink>
              </div>
            </div>
            <div className="app-header__far-right">
              {this.renderSearch()}
              {this.renderAccountMenu()}
              {this.renderCartMenu()}
            </div>
          </div>
        </header>

        <LoadingIndicator numberOfActiveRequests={this.props.numberOfActiveRequests} />

        {!this.props.isOnSearchPage &&
          !this.props.isOnAccountPage &&
          menus.map(this.renderSubheader)}

        {(this.state.isShowingLoginModal || this.state.isShowingSignupModal) &&
          this.renderLoginModal()}
        {this.renderFlyoutCart()}

        <FollowUpModal
          emailEnteredEvent={Event.Wunderkind.EMAIL_ENTERED}
          siteLocation={SiteLocation.WUNDERKIND_MODAL}
        />
      </Fragment>
    )
  }
}

function mapStateToProps(state) {
  const currentPath = getCurrentPath(state)

  setUserActivationStatus(state?.session?.visitor?.segment)

  const gender = state.session.user?.gender || 'female'

  return {
    numberOfActiveRequests: state.activeRequests,
    numberOfLineItems: getLineItemCount(state.cart),
    menus: state.menus.main,
    currentPath,
    isIpad: navigator.userAgent.includes('iPad'),
    isSignedIn: !!state.session.user,
    isAdminUser: getIsAdminUser(state.session.user),
    userName: state.session.user ? state.session.user.first_name : null,
    gender,
    isCartOpen: state.cart.isOpen,
    isOnSearchPage: currentPath === '/search',
    isOnAccountPage: currentPath.includes('/account'),
    lastPath: getLastPath(state),
    lineItemAdded: !!state.cart.isBlocked,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    logOut: () => {
      dispatch(logOut()).then(() => routeTo('/', { hard: true }))
    },
    openCart: () => dispatch(openCart()),
    closeCart: () => dispatch(closeCart()),
  }
}

export default currencyAware(connect(mapStateToProps, mapDispatchToProps)(HeaderV2))

export { HEADER_HEIGHT, SUBHEADER_HEIGHT }
